import { useEffect } from 'react'
import { ConnectAccountManagement, ConnectComponentsProvider } from '@stripe/react-connect-js'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import StripeConnectEmbeddedSurfaceSkeleton from '@/components/stripe-connect/StripeConnectEmbeddedSurfaceSkeleton'
import StripeConnectPaintedSurface from '@/components/stripe-connect/StripeConnectPaintedSurface'
import StripeConnectSection from '@/components/stripe-connect/StripeConnectSection'
import { STRIPE_CONNECT_ACCOUNT_SECTION } from '@/components/stripe-connect/stripeConnectSurfaceContent'
import {
  STRIPE_CONNECT_GENERIC_MAX_HYDRATION_MS,
  useStripeConnectManager,
} from '@/components/stripe-connect/useStripeConnectManager'
import { AlertTriangle, RefreshCw } from 'lucide-react'

export default function StripeConnectAccountManager({
  organizationId,
  accountContext = 'current',
  onSessionConflict,
  onReadyChange,
  showSectionHeader = true,
}) {
  const {
    connectInstance,
    isEmbeddedSurfaceHydrating,
    isLoading,
    loadErrorMessage,
    handleLoadError,
    handleLoaderStart,
    handleSurfacePainted,
    renderAttemptKey,
    retryLoad,
  } = useStripeConnectManager({
    organizationId,
    accountContext,
    onSessionConflict,
    maxHydrationMs: STRIPE_CONNECT_GENERIC_MAX_HYDRATION_MS,
    surface: 'account',
    toastTitle: 'Unable to load Stripe account tools',
  })
  const showPlaceholder = isLoading || isEmbeddedSurfaceHydrating
  const isRenderable = Boolean(loadErrorMessage) || (Boolean(connectInstance) && !showPlaceholder)

  useEffect(() => {
    onReadyChange?.(isRenderable)
  }, [isRenderable, onReadyChange])

  if (loadErrorMessage) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Stripe account tools are unavailable right now</AlertTitle>
        <AlertDescription>
          <p>{loadErrorMessage}</p>
          <Button variant="outline" size="sm" className="mt-2" onClick={retryLoad}>
            <RefreshCw className="size-3.5" aria-hidden="true" />
            Try again
          </Button>
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <StripeConnectSection
      title={STRIPE_CONNECT_ACCOUNT_SECTION.title}
      description={STRIPE_CONNECT_ACCOUNT_SECTION.description}
      showHeader={showSectionHeader}
    >
      <StripeConnectPaintedSurface
        minHeightClassName={STRIPE_CONNECT_ACCOUNT_SECTION.minHeightClassName}
        onLoaderStart={handleLoaderStart}
        onSurfacePainted={handleSurfacePainted}
        placeholder={<StripeConnectEmbeddedSurfaceSkeleton rows={3} />}
        renderAttemptKey={renderAttemptKey}
        showPlaceholder={showPlaceholder}
      >
        {connectInstance
          ? ({ handleLoaderStart: handlePaintAwareLoaderStart }) => (
              <ConnectComponentsProvider connectInstance={connectInstance}>
                <ConnectAccountManagement
                  key={renderAttemptKey}
                  collectionOptions={{
                    fields: 'currently_due',
                    futureRequirements: 'include',
                  }}
                  onLoadError={handleLoadError}
                  onLoaderStart={handlePaintAwareLoaderStart}
                />
              </ConnectComponentsProvider>
            )
          : null}
      </StripeConnectPaintedSurface>
    </StripeConnectSection>
  )
}
